import { IZ_UNIT } from "@/utils/constants";
import { getNavTemplateUrl } from "@/utils/commonutil";

type CategoryPathInput = {
  ids?: Array<string | number>;
  names?: string[];
};

type ProductParentInput = {
  izUnit?: number;
  prodGroup?: string | number | null;
  prodGroupName?: string | null;
  fullCategoryPath?: CategoryPathInput;
};

type CategoryParentInput = {
  ids: Array<string | number>;
  routeType?: string;
  suffix?: string;
};

const hasValue = (value: unknown) => value !== null && value !== undefined && value !== "";

const encodePathSegment = (value: string | number) => encodeURIComponent(String(value));

const normalizeCategoryPath = (path?: CategoryPathInput) => {
  return {
    ids: [...(path?.ids ?? [])].reverse(),
    names: [...(path?.names ?? [])].reverse(),
  };
};

const getLeafId = (ids: Array<string | number>) => ids[ids.length - 1];

const dedupePaths = (paths: string[]) => {
  return paths.filter((path, index) => path && path !== paths[index - 1]);
};

export const buildGroupRouteProductQuery = (
  routeSegments: Array<string | number>,
): { groupId: string | number } | null => {
  const groupId = routeSegments[2];
  const isPartCategoryRoute =
    routeSegments[0] === "g" && routeSegments.at(-1) === "c2";

  if (routeSegments[0] !== "g" || isPartCategoryRoute || !hasValue(groupId)) {
    return null;
  }

  return { groupId };
};

export const resolvePartCategoryRouteIndex = (
  categories: Array<{ id?: string | number }>,
  routeCategoryId?: unknown,
) => {
  if (!categories.length) {
    return -1;
  }

  if (!hasValue(routeCategoryId)) {
    return 0;
  }

  return categories.findIndex(
    (category) => String(category.id) === String(routeCategoryId),
  );
};

export const resolveDirectPartCategoryGroupId = (
  category:
    | { id?: string | number; children?: unknown[] }
    | null
    | undefined,
): string | number | null => {
  if (
    !hasValue(category?.id) ||
    (Array.isArray(category?.children) && category.children.length > 0)
  ) {
    return null;
  }

  return category.id;
};

export const mergeProductGroupsIntoCategories = <
  TCategory extends { id?: string | number; group?: unknown[] },
  TGroup extends Record<string, unknown>,
>(
  categories: TCategory[],
  groupsByCategory: Record<string, TGroup[]> | null | undefined,
) => {
  return categories.map((category) => {
    const latestGroups = hasValue(category.id)
      ? groupsByCategory?.[String(category.id)]
      : undefined;

    return {
      ...category,
      group: Array.isArray(latestGroups) ? [...latestGroups] : [],
    };
  });
};

export const createLatestRequestGate = () => {
  let latestRequestId = 0;

  return {
    begin: () => {
      latestRequestId += 1;
      return latestRequestId;
    },
    invalidate: () => {
      latestRequestId += 1;
    },
    isCurrent: (requestId: number) => requestId === latestRequestId,
  };
};

export const HOME_CATEGORY_QUERY_PARAM = "openCatId";

export const buildHomeCategoryUrl = (topCategoryId?: string | number) => {
  if (!hasValue(topCategoryId)) {
    return "/";
  }

  return `/?${HOME_CATEGORY_QUERY_PARAM}=${encodePathSegment(topCategoryId)}`;
};

export const buildProductGroupUrl = (
  leafCategoryId: string | number,
  groupId: string | number,
  groupName: string,
  izUnit?: number,
) => {
  const suffix = izUnit === IZ_UNIT.PART ? "g" : "c";

  return `/prod/cat/g/${leafCategoryId}/${groupId}/${encodePathSegment(groupName)}/${suffix}`;
};

export const buildProductCategoryLeafUrl = (
  ids: Array<string | number>,
  izUnit?: number,
) => {
  const leafId = getLeafId(ids);

  if (!hasValue(leafId)) {
    return "/";
  }

  if (izUnit === IZ_UNIT.PART) {
    return `/prod/cat/c2/${leafId}`;
  }

  const topId = ids[0];
  const secondId = ids[1] ?? leafId;
  const templateUrl = getNavTemplateUrl(topId, secondId);

  if (templateUrl === "/prod/cat/p/") {
    return `/prod/cat/p/${leafId}`;
  }

  if (leafId !== secondId) {
    return `${templateUrl}${secondId}?catId=${leafId}`;
  }

  return `${templateUrl}${secondId}`;
};

export const buildProductCategoryUrlChain = (
  ids: Array<string | number>,
  izUnit?: number,
) => {
  const categoryIds = ids.filter(hasValue);

  if (categoryIds.length <= 1) {
    return [buildHomeCategoryUrl(categoryIds[0])];
  }

  const paths = [buildHomeCategoryUrl(categoryIds[0])];

  for (let depth = 2; depth <= categoryIds.length; depth += 1) {
    paths.push(buildProductCategoryLeafUrl(categoryIds.slice(0, depth), izUnit));
  }

  return dedupePaths(paths);
};

export const buildProductParentUrlChain = (product: ProductParentInput) => {
  const { ids } = normalizeCategoryPath(product.fullCategoryPath);
  const leafCategoryId = getLeafId(ids);

  if (!hasValue(leafCategoryId)) {
    return ["/"];
  }

  const paths = buildProductCategoryUrlChain(ids, product.izUnit);

  if (hasValue(product.prodGroup) && hasValue(product.prodGroupName)) {
    paths.push(
      buildProductGroupUrl(
        leafCategoryId,
        product.prodGroup as string | number,
        String(product.prodGroupName),
        product.izUnit,
      ),
    );
  }

  return dedupePaths(paths);
};

export const buildProductParentUrl = (product: ProductParentInput) => {
  return buildProductParentUrlChain(product).at(-1) ?? "/";
};

export const buildCategoryLogicalParentUrlChain = ({
  ids,
  routeType,
  suffix,
}: CategoryParentInput) => {
  if (!ids.length) {
    return ["/"];
  }

  const isPartRoute =
    routeType === "c2" || (routeType === "g" && (suffix === "c2" || suffix === "g"));
  const izUnit = isPartRoute ? IZ_UNIT.PART : IZ_UNIT.UNIT;

  if (routeType === "g") {
    return buildProductCategoryUrlChain(ids, izUnit);
  }

  const parentIds = ids.slice(0, -1);

  if (parentIds.length <= 1) {
    return [buildHomeCategoryUrl(parentIds[0])];
  }

  return buildProductCategoryUrlChain(parentIds, izUnit);
};

export const buildCategoryLogicalParentUrl = (input: CategoryParentInput) => {
  return buildCategoryLogicalParentUrlChain(input).at(-1) ?? "/";
};

export const useProductLogicalNavigation = () => {
  const route = useRoute();
  const router = useRouter();

  const resolveFullPath = (path: string) => router.resolve(path).fullPath;

  const getBrowserFullPath = () =>
    `${window.location.pathname}${window.location.search}${window.location.hash}`;

  const areSamePaths = (left?: unknown, right?: string[]) => {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((path, index) => path === right[index])
    );
  };

  const ensureLogicalBackStack = (parentPaths: string[]) => {
    if (!import.meta.client || !parentPaths.length) {
      return;
    }

    const currentFullPath = route.fullPath;
    if (
      getBrowserFullPath() !== currentFullPath
    ) {
      return;
    }

    const parentFullPaths = parentPaths.reduce<string[]>((paths, path) => {
      const fullPath = resolveFullPath(path);

      if (!fullPath || fullPath === currentFullPath || fullPath === paths.at(-1)) {
        return paths;
      }

      paths.push(fullPath);
      return paths;
    }, []);

    const state = window.history.state ?? {};
    const existingBackPath = typeof state.back === "string" ? state.back : null;
    const pathsToInject =
      existingBackPath && parentFullPaths[0] === existingBackPath
        ? parentFullPaths.slice(1)
        : parentFullPaths;
    const immediateParentFullPath = parentFullPaths.at(-1);

    if (!immediateParentFullPath) {
      return;
    }

    if (
      state.current === currentFullPath &&
      state.back === immediateParentFullPath &&
      state.marcoLogicalChainFor === currentFullPath &&
      areSamePaths(state.marcoLogicalParents, parentFullPaths)
    ) {
      return;
    }

    if (!pathsToInject.length) {
      return;
    }

    const position =
      typeof state.position === "number" ? state.position : window.history.length - 1;

    pathsToInject.forEach((parentFullPath, index) => {
      const parentIndex = parentFullPaths.indexOf(parentFullPath);
      const entryState = {
        ...state,
        back: parentFullPaths[parentIndex - 1] ?? state.back ?? null,
        current: parentFullPath,
        forward: parentFullPaths[parentIndex + 1] ?? currentFullPath,
        position: position + index,
        replaced: index === 0 ? state.replaced ?? true : false,
        scroll: null,
        marcoLogicalChainFor: parentFullPath,
        marcoLogicalParents: parentFullPaths.slice(0, index),
      };

      if (index === 0) {
        window.history.replaceState(entryState, "", parentFullPath);
        return;
      }

      window.history.pushState(entryState, "", parentFullPath);
    });

    window.history.pushState(
      {
        ...state,
        back: immediateParentFullPath,
        current: currentFullPath,
        forward: null,
        position: position + parentFullPaths.length,
        replaced: false,
        scroll: null,
        marcoLogicalChainFor: currentFullPath,
        marcoLogicalParents: parentFullPaths,
      },
      "",
      currentFullPath,
    );
  };

  const ensureLogicalBackFallback = (parentPath = "/") => {
    if (!import.meta.client || window.history.state?.back) {
      return;
    }

    ensureLogicalBackStack([parentPath]);
  };

  const ensureLogicalBackEntry = (parentPath: string) => {
    ensureLogicalBackStack([parentPath]);
  };

  return {
    ensureLogicalBackEntry,
    ensureLogicalBackFallback,
    ensureLogicalBackStack,
  };
};
